import type { SelectableItem } from '@physitrack/championhealth-types';

import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import './Style.css';

import type { AssessmentValues } from '../../types/AssessmentPageItem';

import { Alert } from '../../../shared/components/alert/Alert';
import ModalTrigger from '../../../shared/modal/ModalTrigger';
import { ValidationContext } from '../../validation/ValidationContext';

type RadioProps = {
  questionid: string;
  width: string;
  questionTitle: string;
  options: SelectableItem[];
  onSetValue: (value: { id: string; value: string }) => void;
  assessmentValues: AssessmentValues;
  modalText?: string;
  onOpenModal?: () => void;
  questionTitleSuffix?: string;
  skipText?: string;
  fadeIn?: string;
};

export function Radio({
  questionid,
  width,
  questionTitle,
  options,
  onSetValue,
  assessmentValues,
  modalText,
  onOpenModal,
  questionTitleSuffix,
  skipText,
  fadeIn,
}: RadioProps) {
  const { t } = useTranslation();
  const [skipActive, setSkipActive] = useState('');
  const [blockOpacity, setBlockOpacity] = useState(0);
  const [isError, setIsError] = useState(false);

  const { registerValidation } = useContext(ValidationContext);

  function toggleSkipActive() {
    if (skipActive === 'active') {
      setSkipActive('');
      onSetValue({ id: questionid, value: '' });
    } else {
      setSkipActive('active');
      onSetValue({ id: questionid, value: 'skipped' });
    }
  }

  function saveValue(event) {
    onSetValue({ id: questionid, value: event.target.value });
    setIsError(false);
    if (event.target.value !== 'skipped') {
      setSkipActive('');
    }
  }

  const currentValue = assessmentValues[questionid];

  useEffect(() => {
    if (currentValue) {
      if (currentValue === 'skipped') {
        setSkipActive('active');
      }

      if (currentValue !== undefined && currentValue !== null && currentValue !== '') {
        setIsError(false);
      }
    }
  }, [currentValue]);

  useEffect(() => {
    setTimeout(() => {
      setBlockOpacity(1);
    }, 100);
  }, []);

  useEffect(() => {
    registerValidation(() => {
      const isValid = currentValue !== undefined && currentValue !== null && currentValue !== '';
      setIsError(!isValid);
      return isValid;
    });
  }, [currentValue, registerValidation]);

  return (
    <div className={`req question_block${width}`}>
      <fieldset
        id={questionid}
        style={{ opacity: fadeIn ? blockOpacity : '1' }}
        data-input-type="radio-question"
      >
        <legend id={`${questionid}-legend`} className="assessment_question_title">
          {isError && <span className="srOnly">{t('assessment.error.inline.info')}</span>}
          {questionTitle}
        </legend>
        {modalText && (
          <ModalTrigger
            modalText={modalText}
            onOpenModal={onOpenModal}
            question={questionid}
            questionTitle={questionTitle}
          />
        )}

        {questionTitleSuffix && <span className="titleSuffix">{questionTitleSuffix}</span>}

        {skipText && (
          <button
            type="button"
            className={`suffixLabel ${skipActive}`}
            aria-pressed={skipActive === 'active'}
            onClick={toggleSkipActive}
          >
            {skipText}
          </button>
        )}

        <div className="answer_block_container">
          <div className="radio answer_block fs-exclude">
            {options.map((option) => {
              const checked = String(assessmentValues[questionid]) === String(option.id);
              const labelId = `${questionid}_${option.id}_label`;
              return (
                <div className="radioItem" key={option.id}>
                  <input
                    id={`${questionid}_${option.id}`}
                    type="radio"
                    name={questionid}
                    value={option.id}
                    onChange={saveValue}
                    checked={checked}
                    aria-labelledby={labelId}
                  />
                  <label id={labelId} htmlFor={`${questionid}_${option.id}`}>
                    {option.title}
                  </label>
                </div>
              );
            })}
          </div>
        </div>
        <Alert
          id={`${questionid}-error`}
          isVisible={isError}
          message={t('assessment.error.inline.info.one')}
          type="error"
          isRoleAlert
        />
      </fieldset>
    </div>
  );
}
